import { Link } from "react-router-dom";
import { ArrowLeft, Check, Quote } from "lucide-react";
import PublicLayout from "./PublicLayout";
import { AURORA_SUPPORT_EMAIL, AURORA_SUPPORT_CONTACT_PATH } from "../../lib/contact";

function LinkedBody({ text }) {
  const email = AURORA_SUPPORT_EMAIL;
  if (!text || !text.includes(email)) {
    return <>{text}</>;
  }
  const parts = text.split(email);
  return (
    <>
      {parts.map((chunk, idx) => (
        <span key={idx}>
          {chunk}
          {idx < parts.length - 1 && (
            <Link
              to={AURORA_SUPPORT_CONTACT_PATH}
              className="text-[#FF3B6B] hover:underline"
            >
              {email}
            </Link>
          )}
        </span>
      ))}
    </>
  );
}

function Section({ heading, body, bullets }) {
  return (
    <section className="mt-14">
      <h2
        style={{ fontFamily: "'Bodoni Moda', serif" }}
        className="text-2xl md:text-3xl text-white tracking-tight mb-4"
      >
        {heading}
      </h2>
      {body && (
        <p
          className="text-white/70 text-base md:text-lg leading-relaxed"
          style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
        >
          <LinkedBody text={body} />
        </p>
      )}
      {bullets && bullets.length > 0 && (
        <ul className="mt-5 space-y-3">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-3">
              <span className="mt-1.5 shrink-0 w-4 h-4 rounded-full bg-[#FF3B6B]/15 border border-[#FF3B6B]/40 flex items-center justify-center">
                <Check size={9} className="text-[#FF3B6B]" strokeWidth={3} />
              </span>
              <span
                className="text-white/75 text-base leading-relaxed"
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              >
                <LinkedBody text={b} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function PressStrip({ logos }) {
  return (
    <section className="mt-16" data-testid="press-strip">
      <p
        className="text-white/50 text-[11px] uppercase tracking-[0.25em] mb-6 text-center"
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
      >
        Featured &amp; discussed in
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {logos.map((l) => (
          <div
            key={l.name}
            className="group aspect-[3/1.4] rounded-xl border border-white/[0.08] bg-white/[0.02] flex items-center justify-center hover:border-white/20 transition-colors"
            data-testid={`press-logo-${l.name.replace(/\W/g, "-").toLowerCase()}`}
          >
            <div className="text-center">
              <p
                style={{ fontFamily: "'Bodoni Moda', serif" }}
                className="text-white/85 text-base tracking-tight"
              >
                {l.name}
              </p>
              <p
                className="text-white/40 text-[9px] uppercase tracking-[0.2em] mt-0.5"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                {l.tagline}
              </p>
            </div>
          </div>
        ))}
      </div>
      <p className="text-white/40 text-[11px] text-center mt-4 italic">
        Coverage placeholders — replace with real press logos as they publish.
      </p>
    </section>
  );
}

export function StoryCards({ stories }) {
  return (
    <section className="mt-16" data-testid="story-cards">
      <div className="mb-6">
        <h2
          style={{ fontFamily: "'Bodoni Moda', serif" }}
          className="text-3xl md:text-4xl text-white tracking-tight"
          data-testid="story-cards-heading"
        >
          Real Love Stories
        </h2>
      </div>

      {/* Single vertical column — cards stack top-to-bottom, browser handles the scroll */}
      <div className="flex flex-col gap-6" data-testid="story-cards-column">
        {stories.map((s, sIdx) => (
          <article
            key={s.image}
            className="rounded-2xl overflow-hidden border border-white/[0.08] bg-[#0F0F14] shadow-[0_10px_40px_rgba(0,0,0,0.4)] max-w-xl mx-auto w-full"
            data-testid={`story-card-${s.title.replace(/\W/g, "-").toLowerCase()}`}
          >
            {/* Uniform photo area — all sources pre-normalized to 4:3 (800x600) so every card fills perfectly */}
            <div className="relative w-full overflow-hidden bg-black" style={{ aspectRatio: "4 / 3" }}>
              <img
                src={s.image}
                alt={s.title}
                loading={sIdx < 2 ? "eager" : "lazy"}
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
            </div>

            {/* Info panel */}
            <div className="px-5 py-4 sm:px-6 sm:py-5">
              <p
                className="text-[#FF3B6B] text-[11px] uppercase tracking-[0.25em] mb-1"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                {s.location}
              </p>
              <h3
                style={{ fontFamily: "'Bodoni Moda', serif" }}
                className="text-white text-2xl tracking-tight mb-3"
              >
                {s.title}
              </h3>
              <div className="flex gap-2">
                <Quote size={16} className="text-[#FF3B6B]/70 shrink-0 mt-1" />
                <p
                  className="text-white/80 text-[14px] leading-relaxed"
                  style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                >
                  &ldquo;{s.quote}&rdquo;
                </p>
              </div>
              <p
                className="mt-4 text-white/40 text-[10px] uppercase tracking-[0.22em]"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                {s.byline}
              </p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function StoryGroups({ groups }) {
  return (
    <section className="mt-16 -mx-6 md:-mx-10" data-testid="story-groups">
      {groups.map((group, gIdx) => (
        <div key={group.theme} className={gIdx === 0 ? "" : "mt-16"}>
          <div className="px-6 md:px-10">
            <p
              className="text-[#FF3B6B] text-[11px] uppercase tracking-[0.25em] mb-2"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              Theme {String(gIdx + 1).padStart(2, "0")}
            </p>
            <h2
              style={{ fontFamily: "'Bodoni Moda', serif" }}
              className="text-3xl md:text-4xl text-white tracking-tight"
            >
              {group.theme}
            </h2>
            {group.subtitle && (
              <p
                className="mt-2 text-white/60 text-sm md:text-base leading-relaxed max-w-2xl"
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              >
                {group.subtitle}
              </p>
            )}
          </div>

          <div
            className="mt-6 flex gap-4 md:gap-5 overflow-x-auto snap-x snap-mandatory px-6 md:px-10 pb-4 aurora-scroll-row"
            data-testid={`story-row-${group.theme.replace(/\W/g, "-").toLowerCase()}`}
          >
            {group.stories.map((s, sIdx) => (
              <article
                key={`${group.theme}-${s.image}`}
                className="group shrink-0 snap-start w-[78%] sm:w-[46%] md:w-[300px] rounded-2xl overflow-hidden border border-white/[0.08] bg-white/[0.02] hover:border-white/20 transition-colors"
                data-testid={`story-card-${s.title.replace(/\W/g, "-").toLowerCase()}`}
              >
                <div className="aspect-[4/5] overflow-hidden relative">
                  <img
                    src={s.image}
                    alt={s.title}
                    loading={gIdx === 0 && sIdx < 2 ? "eager" : "lazy"}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-4">
                    <p
                      className="text-[#FF3B6B] text-[10px] uppercase tracking-[0.25em] mb-1"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {s.location}
                    </p>
                    <h3
                      style={{ fontFamily: "'Bodoni Moda', serif" }}
                      className="text-white text-xl tracking-tight"
                    >
                      {s.title}
                    </h3>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex gap-2">
                    <Quote size={13} className="text-[#FF3B6B]/60 shrink-0 mt-1" />
                    <p
                      className="text-white/75 text-[13px] leading-relaxed italic"
                      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                    >
                      {s.quote}
                    </p>
                  </div>
                  <p
                    className="mt-3 text-white/40 text-[9px] uppercase tracking-[0.2em]"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    {s.byline}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

export default function PlaceholderPage({
  eyebrow,
  title,
  description,
  sections = [],
  press,
  stories,
  storyGroups,
  testId,
}) {
  return (
    <PublicLayout>
      <section
        className="relative overflow-hidden"
        data-testid={testId || "placeholder-page"}
      >
        {/* Background aurora glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 left-1/4 w-[600px] h-[600px] rounded-full bg-[#FF3B6B]/[0.07] blur-[140px]" />
          <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full bg-[#7C3AED]/[0.06] blur-[140px]" />
        </div>

        <div className="relative max-w-3xl mx-auto px-6 md:px-10 pt-24 pb-32 md:pt-32 md:pb-40">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/50 hover:text-white text-xs uppercase tracking-[0.2em] mb-10 transition-colors"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
            data-testid="placeholder-back-home"
          >
            <ArrowLeft size={12} />
            Back to Aurora
          </Link>

          {eyebrow && (
            <p
              className="text-[#FF3B6B] text-[11px] uppercase tracking-[0.3em] mb-5"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
              data-testid="placeholder-eyebrow"
            >
              {eyebrow}
            </p>
          )}

          <h1
            style={{ fontFamily: "'Bodoni Moda', serif" }}
            className="text-5xl sm:text-6xl md:text-7xl tracking-tighter text-white leading-[1.05]"
            data-testid="placeholder-title"
          >
            {title}
          </h1>

          <div
            className="mt-8 h-[1px] w-16 bg-gradient-to-r from-[#FF3B6B] to-transparent"
            aria-hidden
          />

          {description && (
            <p
              className="mt-8 text-white/70 text-lg md:text-xl leading-relaxed"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              data-testid="placeholder-description"
            >
              {typeof description === "string" ? <LinkedBody text={description} /> : description}
            </p>
          )}

          {sections.map((s, idx) => <Section key={idx} {...s} />)}
          {press && press.length > 0 && <PressStrip logos={press} />}
          {storyGroups && storyGroups.length > 0 && <StoryGroups groups={storyGroups} />}
          {stories && stories.length > 0 && <StoryCards stories={stories} />}

          <div className="mt-16 p-6 rounded-2xl border border-white/[0.08] bg-white/[0.02]">
            <p
              className="text-white/50 text-[11px] uppercase tracking-[0.25em] mb-2"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              Questions?
            </p>
            <p className="text-white/75 text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Reach the Aurora team any time — write to us at{" "}
              <Link
                to={AURORA_SUPPORT_CONTACT_PATH}
                className="text-[#FF3B6B] hover:underline"
              >
                {AURORA_SUPPORT_EMAIL}
              </Link>{" "}
              — we reply within one business day.
            </p>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
